import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConfig } from '@/lib/siteData';

export interface AnalyticsConfig {
  enabled: boolean;
  scriptSrc: string;
  dataDomain: string;
  scriptAttributes?: Record<string, string>;
  excludeAdmin: boolean;
  /**
   * Plausible's "new" snippet (async `pa-*.js` + an inline `plausible.init()`).
   * When true the external script is loaded `async`, `data-domain` is omitted
   * (the domain is baked into the file), and the init stub is run so the script
   * actually starts tracking — loading the URL alone is a no-op. When false the
   * classic `defer` + `data-domain` tag is used (Plausible `script.js`, Umami…).
   */
  runInit?: boolean;
}

const DEFAULT_ANALYTICS: AnalyticsConfig = {
  enabled: false,
  scriptSrc: '',
  dataDomain: '',
  scriptAttributes: {},
  excludeAdmin: true,
  runInit: false,
};

const SCRIPT_ID = 'ck-analytics';

/** Minimal shape of the global Plausible queue stub. */
interface PlausibleFn {
  (...args: unknown[]): void;
  q?: unknown[][];
  o?: unknown;
  init?: (i?: unknown) => void;
}

/**
 * Injects a privacy-friendly analytics script (Plausible, Umami, or
 * compatible) whose source + domain are configurable via /admin/settings.
 * No cookies are set by the trackers we support, so no consent banner is
 * required — see /privacy for the policy text.
 *
 * The script is added once on mount and updated in place if the admin
 * swaps src or domain. Pages under /admin/* are excluded by default to
 * keep admin sessions out of the public traffic stream.
 */
export function usePublicAnalytics(): void {
  const cfg = useConfig<AnalyticsConfig>('analytics_config', DEFAULT_ANALYTICS);
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const onAdminPage = pathname.startsWith('/admin');
    const shouldInject =
      cfg.enabled && cfg.scriptSrc.trim().length > 0 && !(cfg.excludeAdmin && onAdminPage);

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (!shouldInject) {
      if (existing) existing.remove();
      return;
    }

    // Already injected for this src? Don't re-inject — avoids analytics
    // double-counting (and re-running init) on SPA route changes. For the new
    // Plausible script the domain lives inside the file, so match on src only;
    // for the classic tag we also re-inject when data-domain changes.
    if (existing && existing.src === cfg.scriptSrc) {
      if (cfg.runInit || existing.dataset.domain === cfg.dataDomain) return;
    }
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = cfg.scriptSrc;

    if (cfg.runInit) {
      // Plausible new snippet: async external script + inline init stub. The
      // external pa-*.js only initializes when `window.plausible.o` is set (by
      // init()), so we run the stub + init() ourselves before appending.
      script.async = true;
      const w = window as unknown as { plausible?: PlausibleFn };
      if (!w.plausible) {
        const stub: PlausibleFn = (...args: unknown[]) => {
          (stub.q = stub.q || []).push(args);
        };
        w.plausible = stub;
      }
      w.plausible.init = w.plausible.init || ((i?: unknown) => {
        (w.plausible as PlausibleFn).o = i || {};
      });
      w.plausible.init();
    } else {
      // Classic Plausible/Umami tag.
      script.defer = true;
      if (cfg.dataDomain) script.dataset.domain = cfg.dataDomain;
    }

    if (cfg.scriptAttributes) {
      for (const [k, v] of Object.entries(cfg.scriptAttributes)) {
        if (!k.startsWith('data-')) continue;
        script.setAttribute(k, v);
      }
    }
    document.head.appendChild(script);
  }, [
    cfg.enabled,
    cfg.scriptSrc,
    cfg.dataDomain,
    cfg.excludeAdmin,
    cfg.scriptAttributes,
    cfg.runInit,
    pathname,
  ]);
}
